import { Component, OnInit } from '@angular/core';
import { NavController } from '@ionic/angular';
import _ from 'lodash';

import { routes } from 'src/app/constants';
import { AGENT_HANDOFF_MODES, AiFeatures, Tenant } from 'src/models';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';

@Component({
  selector: 'app-admin-ai',
  templateUrl: './admin-ai.component.html',
  styleUrls: ['./admin-ai.component.scss'],
})
export class AdminAiComponent implements OnInit {
  aiFeatures: AiFeatures;
  interactiveModeCustomPrompt?: string;
  tenant: Partial<Tenant>;
  AGENT_HANDOFF_MODES = AGENT_HANDOFF_MODES;
  availableLanguages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'nl', name: 'Dutch' },
    { code: 'sv', name: 'Swedish' },
    { code: 'da', name: 'Danish' },
    { code: 'no', name: 'Norwegian' },
    { code: 'fi', name: 'Finnish' },
    { code: 'pl', name: 'Polish' },
    { code: 'el', name: 'Greek' },
    { code: 'hu', name: 'Hungarian' },
    { code: 'cs', name: 'Czech' },
    { code: 'ro', name: 'Romanian' },
    { code: 'bg', name: 'Bulgarian' },
    { code: 'hr', name: 'Croatian' },
    { code: 'sr', name: 'Serbian' },
    { code: 'sk', name: 'Slovak' },
    { code: 'lt', name: 'Lithuanian' },
    { code: 'sl', name: 'Slovenian' },
    { code: 'lv', name: 'Latvian' },
    { code: 'et', name: 'Estonian' },
    { code: 'ru', name: 'Russian' },
    { code: 'uk', name: 'Ukrainian' },
    { code: 'sw', name: 'Swahili' },
    { code: 'tr', name: 'Turkish' },
    { code: 'ar', name: 'Arabic' },
    { code: 'zh', name: 'Chinese' },
    { code: 'hi', name: 'Hindi' },
    { code: 'bn', name: 'Bengali' },
    { code: 'vi', name: 'Vietnamese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'th', name: 'Thai' },
    { code: 'id', name: 'Indonesian' },
  ]; // Add a list of available languages
  constructor(
    private authService: AuthService,
    private navController: NavController,
    private msgService: MessageService,
  ) {}

  ngOnInit(): void {
    this.tenant = this.authService.tenant;
    this.aiFeatures = this.tenant.aiFeatures ?? {};

    if (this.aiFeatures.temperature == null) {
      this.aiFeatures.temperature = 0;
    }

    if (this.aiFeatures.agentStaticPrefix == null) {
      this.aiFeatures.agentStaticPrefix = 'Hang on, looking into this...';
    }

    if (this.aiFeatures.agentStaticPrefixOutsideWorkingHours == null) {
      this.aiFeatures.agentStaticPrefixOutsideWorkingHours = '';
    }

    if (this.aiFeatures.maxAutoTags == null) {
      this.aiFeatures.maxAutoTags = 3; // Default to 3 tags
    }

    // Initialize generatedContentLanguage if not set
    if (this.aiFeatures.generatedContentLanguage === undefined) {
      this.aiFeatures.generatedContentLanguage = 'en'; // Default to English
    }

    this.interactiveModeCustomPrompt = this.tenant.interactiveModeCustomPrompt;
  }

  manageTicketTypes() {
    this.navController.navigateRoot(
      `${routes.DASHBOARD}/${routes.SETTINGS}/projects/${this.tenant.defaultProjectId}/ticket-types`,
    );
  }

  manageTags() {
    this.navController.navigateRoot(`${routes.DASHBOARD}/tags`);
  }

  manageDocs() {
    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.KNOWLEDGE_BASE}`);
  }

  manageAutoresponder() {
    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.SETTINGS}/general`);
  }

  viewTagAnalytics() {
    this.navController.navigateRoot(`${routes.DASHBOARD}/analytics/performance?dimension=tagId`);
  }

  viewTicketTypeAnalytics() {
    this.navController.navigateRoot(`${routes.DASHBOARD}/analytics/performance?dimension=ticketTypeId`);
  }

  viewAIAnalytics() {
    this.navController.navigateRoot(`${routes.DASHBOARD}/analytics/ai`);
  }

  requestAccess(feature: string) {
    window.open(`mailto:sales@unthread.io?subject=Beta Access to ${feature}`, '_blank');
  }

  onChange() {
    setTimeout(() => {
      this.save();
    }, 500);
  }

  async save() {
    await this.authService.updateTenant({
      aiFeatures: this.aiFeatures,
      interactiveModeCustomPrompt: this.interactiveModeCustomPrompt,
    });

    this.msgService.show('Saved!');
  }

  getLlmName() {
    if (!this.tenant.llmConfig?.type) {
      return 'ChatGPT4';
    }
    if (this.tenant.llmConfig.type === 'openai') {
      return 'OpenAI';
    }
    return this.tenant.llmConfig.type;
  }

  compareFn(v1, v2) {
    return v1 === v2 || (v1 === '' && v2 === null);
  }
}
